import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MDBAlert, MDBBtn, MDBBtnGroup, MDBIcon } from 'mdb-react-ui-kit';
import { BOOTH_STATUS, boothKeyOf, boothMatchesFilters, compareBoothNumbers, polygonBounds, polygonCentroid } from '../../../../utils/boothMap';
import { transformMoney } from '../../../../utils/money';
import { formatArea, getStatusLabel, statusVariant } from './boothStatusText';

export const ZOOM_STEPS = [1, 1.5, 2, 2.5, 3, 4];
const DRAG_THRESHOLD_PX = 4;
// Booth numbers are shown at every zoom, each fitted into its booth and kept between these on-screen sizes.
const LABEL_MIN_PX = 6;
const LABEL_MAX_PX = 13;
// average glyph width of the semi-bold label font (in em) and the share of the booth the label may fill
const LABEL_CHAR_EM = 0.62;
const LABEL_FILL_WIDTH = 0.85;
const LABEL_FILL_HEIGHT = 0.55;

function scrollToCenter(element, center) {
  element.scrollLeft = center.x * element.scrollWidth - element.clientWidth / 2;
  element.scrollTop = center.y * element.scrollHeight - element.clientHeight / 2;
}

/**
 * Plan coordinate space: BoothPlan.viewBox ("minX minY w h", SVG hall-plan import) when valid,
 * otherwise "0 0 width height" (legacy raster plans). Returns null when no usable size is known.
 */
export function planViewBox(plan) {
  if (!plan) return null;
  if (typeof plan.viewBox === 'string' && plan.viewBox.trim()) {
    const parts = plan.viewBox.trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every(Number.isFinite) && parts[2] > 0 && parts[3] > 0) {
      return { minX: parts[0], minY: parts[1], width: parts[2], height: parts[3] };
    }
  }
  const width = Number(plan.width) || 0;
  const height = Number(plan.height) || 0;
  if (width > 0 && height > 0) return { minX: 0, minY: 0, width, height };
  return null;
}

/**
 * Font size (plan units) of a booth number: as large as fits into the booth (bounds width / height),
 * clamped to LABEL_MIN_PX..LABEL_MAX_PX on screen. `pxPerUnit` is the current screen scale of the plan;
 * without it (not measured yet) the label only gets the fitted size, capped at `fallbackMax`.
 */
export function fittedLabelSize(boothNumber, bounds, pxPerUnit, fallbackMax) {
  const chars = Math.max(1, String(boothNumber || '').length);
  const boxWidth = Math.max(0, bounds.maxX - bounds.minX);
  const boxHeight = Math.max(0, bounds.maxY - bounds.minY);
  const fit = Math.min((boxWidth * LABEL_FILL_WIDTH) / (chars * LABEL_CHAR_EM), boxHeight * LABEL_FILL_HEIGHT);
  if (!(pxPerUnit > 0)) return Math.min(fit > 0 ? fit : fallbackMax, fallbackMax);
  const min = LABEL_MIN_PX / pxPerUnit;
  const max = LABEL_MAX_PX / pxPerUnit;
  return Math.min(max, Math.max(min, fit));
}

function hasPoints(booth) {
  return !!(booth && typeof booth.points === 'string' && booth.points.trim());
}

function unassignedAreaKey(area) {
  return area.id || area.boothNumber;
}

function areaClassName(booth, { matches, selected, hit, hovered }) {
  const classes = ['booth-area', `booth-area--${statusVariant(booth.status)}`];
  const drawnAsIs = booth.status === BOOTH_STATUS.SOLD || booth.status === BOOTH_STATUS.UNAVAILABLE;
  if (!drawnAsIs && !matches) classes.push('booth-area--filtered-out');
  if (selected) classes.push('booth-area--selected');
  if (hit) classes.push('booth-area--hit');
  if (hovered && !selected) classes.push('booth-area--hovered');
  return classes.join(' ');
}

/** Hover/status line text of a booth (separate parts, rendered with a separator). */
function hoverText(t, booth) {
  const area = formatArea(booth.area);
  const price = booth.price === null || booth.price === undefined ? null : transformMoney(booth.price);
  switch (booth.status) {
    case BOOTH_STATUS.SOLD:
      return booth.exhibitor && booth.exhibitor.name
        ? t('Sold to {{company}} · {{area}} m²', { company: booth.exhibitor.name, area })
        : t('Sold · {{area}} m²', { area });
    case BOOTH_STATUS.FREE:
      return price ? t('Available · {{area}} m² · {{price}}', { area, price }) : t('Available · {{area}} m²', { area });
    case BOOTH_STATUS.HELD_BY_YOU:
      return t('Held for you · {{area}} m²', { area });
    case BOOTH_STATUS.RESERVED_FOR_YOU:
      return t('Reserved for you · {{area}} m²', { area });
    case BOOTH_STATUS.HELD_BY_OTHER:
    case BOOTH_STATUS.RESERVED_FOR_OTHER:
      return t('Reserved · {{area}} m²', { area });
    default:
      return t('Not bookable');
  }
}

function statusIcon(status) {
  switch (status) {
    case BOOTH_STATUS.FREE:
      return 'check-circle';
    case BOOTH_STATUS.HELD_BY_YOU:
    case BOOTH_STATUS.RESERVED_FOR_YOU:
      return 'star';
    case BOOTH_STATUS.HELD_BY_OTHER:
    case BOOTH_STATUS.RESERVED_FOR_OTHER:
      return 'hourglass-half';
    case BOOTH_STATUS.SOLD:
      return 'lock';
    default:
      return 'ban';
  }
}

/**
 * Hall plan with booth polygons (3 stacked layers like the design):
 *  1. colored status polygons (SVG, viewBox = BoothPlan coordinate space: plan.viewBox or "0 0 width height")
 *  2. the plan image on top in its own colours (pointer-events none, multiply blend so white turns transparent)
 *  3. a transparent hit layer SVG with the click/hover polygons and the booth numbers (every zoom, fitted
 *     into each booth: fittedLabelSize)
 * Zoom via buttons (ZOOM_STEPS), pan by dragging; the status legend sits in the bottom right corner of the
 * plan, the hover line below describes the booth under the cursor (or marks an unassigned area of the plan
 * as not bookable).
 *
 * Props: plan (event.boothPlan), booths (map booths), unassignedAreas, selectedId, hoveredId, hitId
 * (search hit) - all three are boothKeys (boothKeyOf: eventProductId + '#' + booth number, several booths can
 * share one variant product) -, focusKey (changes => center the selected booth), filters, onSelect(booth),
 * onHover(boothKey|null), planUrl (signed image URL), planUrlFailed (the signed URL could not be created),
 * bookingOpen (default true; while false a free booth is labelled "Available" without "book now").
 */
export default function HallPlanMap({
  plan,
  booths,
  unassignedAreas,
  selectedId,
  hoveredId,
  hitId,
  focusKey,
  filters,
  onSelect,
  onHover,
  planUrl,
  planUrlFailed,
  bookingOpen = true,
}) {
  const { t } = useTranslation();

  const viewportRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const pendingCenterRef = useRef(null);

  const [zoom, setZoom] = useState(1);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [panning, setPanning] = useState(false);
  // unassigned plan area under the cursor (local; booth hover stays with the parent via hoveredId/onHover)
  const [hoveredAreaId, setHoveredAreaId] = useState(null);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  const viewBox = useMemo(() => planViewBox(plan), [plan]);
  const minX = viewBox ? viewBox.minX : 0;
  const minY = viewBox ? viewBox.minY : 0;
  const width = viewBox ? viewBox.width : 0;
  const height = viewBox ? viewBox.height : 0;
  const viewBoxAttr = `${minX} ${minY} ${width} ${height}`;

  const mappedBooths = useMemo(() => (booths || []).filter(hasPoints), [booths]);
  const unmappedBooths = useMemo(
    () => (booths || []).filter((b) => !hasPoints(b)).sort((a, b) => compareBoothNumbers(a.boothNumber, b.boothNumber)),
    [booths]
  );
  const otherAreas = useMemo(() => (unassignedAreas || []).filter((a) => a && typeof a.points === 'string' && a.points.trim()), [unassignedAreas]);

  const matchesById = useMemo(() => {
    const result = {};
    for (const booth of booths || []) result[boothKeyOf(booth)] = boothMatchesFilters(booth, filters);
    return result;
  }, [booths, filters]);

  // booths per legend colour (statusVariant)
  const statusCounts = useMemo(() => {
    const result = { free: 0, reserved: 0, mine: 0, sold: 0 };
    for (const booth of booths || []) {
      const variant = statusVariant(booth.status);
      if (variant in result) result[variant] += 1;
    }
    return result;
  }, [booths]);

  const labelPositions = useMemo(() => {
    const result = {};
    for (const booth of mappedBooths) {
      const bounds = polygonBounds(booth.points);
      if (Number.isFinite(Number(booth.labelX)) && Number.isFinite(Number(booth.labelY)) && booth.labelX !== null && booth.labelY !== null) {
        result[boothKeyOf(booth)] = { x: Number(booth.labelX), y: Number(booth.labelY), bounds };
      } else {
        result[boothKeyOf(booth)] = { ...polygonCentroid(booth.points), bounds };
      }
    }
    return result;
  }, [mappedBooths]);

  const canDrawPlan = width > 0 && height > 0 && mappedBooths.length > 0;
  const showMap = canDrawPlan && !!planUrl;

  // ----- viewport size (label font size in plan units) ---------------------------------
  // depends on showMap: the viewport element only exists once the plan URL has arrived
  useEffect(() => {
    const element = viewportRef.current;
    if (!element) return undefined;
    setViewportWidth(element.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => setViewportWidth(element.clientWidth));
    observer.observe(element);
    return () => observer.disconnect();
  }, [showMap]);

  // ----- zoom ---------------------------------------------------------------------------
  const changeZoom = useCallback(
    (nextZoom, centerRatio) => {
      const element = viewportRef.current;
      if (element && !centerRatio) {
        centerRatio = {
          x: (element.scrollLeft + element.clientWidth / 2) / Math.max(element.scrollWidth, 1),
          y: (element.scrollTop + element.clientHeight / 2) / Math.max(element.scrollHeight, 1),
        };
      }
      if (nextZoom === zoomRef.current) {
        if (element && centerRatio) scrollToCenter(element, centerRatio);
        return;
      }
      pendingCenterRef.current = centerRatio || null;
      setZoom(nextZoom);
    },
    []
  );

  // keep the requested point centered after a zoom change
  useLayoutEffect(() => {
    const element = viewportRef.current;
    const center = pendingCenterRef.current;
    if (!element || !center) return;
    pendingCenterRef.current = null;
    scrollToCenter(element, center);
  }, [zoom]);

  const zoomIndex = ZOOM_STEPS.indexOf(zoom);
  const zoomIn = () => changeZoom(ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, zoomIndex + 1)]);
  const zoomOut = () => changeZoom(ZOOM_STEPS[Math.max(0, zoomIndex - 1)]);
  const zoomFit = () => {
    pendingCenterRef.current = null;
    setZoom(1);
    if (viewportRef.current) {
      viewportRef.current.scrollLeft = 0;
      viewportRef.current.scrollTop = 0;
    }
  };

  // center a booth picked outside the map (search hit)
  useEffect(() => {
    if (!focusKey || !canDrawPlan) return;
    const booth = mappedBooths.find((b) => boothKeyOf(b) === selectedId);
    if (!booth) return;
    const bounds = polygonBounds(booth.points);
    const center = { x: ((bounds.minX + bounds.maxX) / 2 - minX) / width, y: ((bounds.minY + bounds.maxY) / 2 - minY) / height };
    changeZoom(Math.max(zoom, 2), center);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  // ----- drag to pan --------------------------------------------------------------------
  const onPointerMove = useCallback((event) => {
    const drag = dragRef.current;
    const element = viewportRef.current;
    if (!drag || !element) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD_PX) return;
    if (!drag.moved) {
      drag.moved = true;
      setPanning(true);
    }
    element.scrollLeft = drag.scrollLeft - dx;
    element.scrollTop = drag.scrollTop - dy;
  }, []);

  const onPointerUp = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
    if (drag && drag.moved) {
      suppressClickRef.current = true;
      setPanning(false);
      // the click (if any) fires synchronously after pointerup; reset afterwards
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }
  }, [onPointerMove]);

  const onPointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    const element = viewportRef.current;
    if (!element) return;
    dragRef.current = { x: event.clientX, y: event.clientY, scrollLeft: element.scrollLeft, scrollTop: element.scrollTop, moved: false };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  };

  useEffect(
    () => () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    },
    [onPointerMove, onPointerUp]
  );

  const onClickCapture = (event) => {
    if (suppressClickRef.current) {
      event.stopPropagation();
      event.preventDefault();
      suppressClickRef.current = false;
    }
  };

  // ----- legend + hover line ------------------------------------------------------------
  const legendItems = [
    { variant: 'free', label: t('Available') },
    { variant: 'reserved', label: t('Reserved') },
    // only once the company holds a booth
    ...(statusCounts.mine > 0 ? [{ variant: 'mine', label: t('Yours') }] : []),
    { variant: 'sold', label: t('Sold') },
  ];

  const renderLegend = (className) => (
    <div className={className} data-testid="booth-map-legend">
      {legendItems.map((item) => (
        <span key={item.variant} className="text-nowrap">
          <span className={`booth-legend-swatch booth-legend-swatch--${item.variant} me-1`} />
          {item.label}
          <span className="text-muted ms-1">({statusCounts[item.variant]})</span>
        </span>
      ))}
    </div>
  );

  const hoveredBooth = (booths || []).find((b) => boothKeyOf(b) === hoveredId) || null;
  const hoveredArea = hoveredAreaId ? otherAreas.find((area) => unassignedAreaKey(area) === hoveredAreaId) || null : null;

  const renderHoverLine = () => {
    if (hoveredArea) {
      return (
        <div className="small d-flex align-items-center flex-wrap gap-2 text-muted">
          <MDBIcon fas icon="ban" />
          {!hoveredArea.unnamed && hoveredArea.boothNumber ? <strong>{hoveredArea.boothNumber}</strong> : null}
          <span>{t('Not bookable')}</span>
        </div>
      );
    }
    if (!hoveredBooth) {
      return <div className="small text-muted d-none d-md-block">{t('Hover over a booth for details, drag to move the plan.')}</div>;
    }
    const drawnAsIs = hoveredBooth.status === BOOTH_STATUS.SOLD || hoveredBooth.status === BOOTH_STATUS.UNAVAILABLE;
    const filteredOut = !drawnAsIs && !matchesById[boothKeyOf(hoveredBooth)];
    return (
      <div className="small d-flex align-items-center flex-wrap gap-2">
        <MDBIcon fas icon={statusIcon(hoveredBooth.status)} className="text-muted" />
        <strong>{t('Booth {{number}}', { number: hoveredBooth.boothNumber })}</strong>
        <span className="text-muted">{hoverText(t, hoveredBooth)}</span>
        {filteredOut && <span className="text-warning">{t('does not match your filters')}</span>}
      </div>
    );
  };

  // ----- fallback without plan ----------------------------------------------------------
  const renderBoothButtons = (list) => (
    <div className="d-flex flex-wrap gap-2">
      {list.map((booth) => {
        const key = boothKeyOf(booth);
        const selected = key === selectedId;
        return (
          <MDBBtn
            key={key}
            size="sm"
            color={selected ? 'primary' : 'light'}
            className={`text-none booth-status-chip--${statusVariant(booth.status)}`}
            title={getStatusLabel(t, booth.status, bookingOpen)}
            onClick={() => onSelect && onSelect(booth)}
            onMouseEnter={() => onHover && onHover(key)}
            onMouseLeave={() => onHover && onHover(null)}
          >
            {booth.boothNumber}
            {booth.area ? <span className="ms-1 opacity-75">({formatArea(booth.area)} m²)</span> : null}
          </MDBBtn>
        );
      })}
    </div>
  );

  if (!showMap) {
    const all = [...(booths || [])].sort((a, b) => compareBoothNumbers(a.boothNumber, b.boothNumber));
    const hasImage = !!(plan && plan.image && plan.image.s3Path);
    let planMessage;
    if (!hasImage) {
      planMessage = (
        <MDBAlert open color="info" className="mb-3">
          {t('No hall plan has been uploaded for this event yet.')}
        </MDBAlert>
      );
    } else if (!canDrawPlan) {
      planMessage = (
        <MDBAlert open color="info" className="mb-3">
          {t('The booth positions have not been added to the hall plan yet.')}
        </MDBAlert>
      );
    } else if (planUrlFailed) {
      planMessage = (
        <MDBAlert open color="warning" className="mb-3">
          {t('The hall plan image could not be loaded.')}
        </MDBAlert>
      );
    } else {
      planMessage = <div className="text-muted small mb-3">{t('Loading hall plan...')}</div>;
    }
    return (
      <div>
        {planMessage}
        {all.length > 0 ? renderBoothButtons(all) : <p className="text-muted">{t('No booths are available for this event yet.')}</p>}
        {renderLegend('d-flex flex-wrap gap-3 small mt-3')}
        <div className="mt-2">{renderHoverLine()}</div>
      </div>
    );
  }

  // screen pixels per plan unit at the current zoom (0 until the viewport is measured)
  const pxPerUnit = viewportWidth > 0 ? (viewportWidth * zoom) / width : 0;
  const hatchSize = Math.max(width, height) / 160;

  return (
    <div>
      <div className="booth-map" style={{ height: 'auto', minHeight: 0 }}>
        <div className="booth-map__toolbar">
          <MDBBtnGroup vertical size="sm" className="shadow-2">
            <MDBBtn color="light" size="sm" onClick={zoomIn} disabled={zoomIndex >= ZOOM_STEPS.length - 1} title={t('Zoom in')}>
              <MDBIcon fas icon="plus" />
            </MDBBtn>
            <MDBBtn color="light" size="sm" disabled className="px-1 text-dark" style={{ opacity: 1 }}>
              {Math.round(zoom * 100)}%
            </MDBBtn>
            <MDBBtn color="light" size="sm" onClick={zoomOut} disabled={zoomIndex <= 0} title={t('Zoom out')}>
              <MDBIcon fas icon="minus" />
            </MDBBtn>
            <MDBBtn color="light" size="sm" onClick={zoomFit} title={t('Fit to view')}>
              <MDBIcon fas icon="expand" />
            </MDBBtn>
          </MDBBtnGroup>
        </div>

        <div
          ref={viewportRef}
          className={panning ? 'booth-map--panning' : undefined}
          style={{
            overflow: 'auto',
            aspectRatio: `${width} / ${height}`,
            maxHeight: '75vh',
            minHeight: 280,
            width: '100%',
            cursor: panning ? 'grabbing' : 'grab',
          }}
          onPointerDown={onPointerDown}
          onClickCapture={onClickCapture}
        >
          <div style={{ position: 'relative', width: `${zoom * 100}%`, aspectRatio: `${width} / ${height}` }}>
            {/* Layer 1: colored status polygons */}
            <svg
              viewBox={viewBoxAttr}
              preserveAspectRatio="none"
              style={{ position: 'absolute', inset: 0 }}
              aria-hidden="true"
            >
              <defs>
                <pattern id="booth-sold-hatch" patternUnits="userSpaceOnUse" width={hatchSize} height={hatchSize} patternTransform="rotate(45)">
                  <rect width={hatchSize} height={hatchSize} fill="#e3e6ea" />
                  <rect width={hatchSize / 2} height={hatchSize} fill="#c4c8cf" />
                </pattern>
              </defs>
              {otherAreas.map((area) => (
                <polygon
                  key={`unassigned-${area.id || area.boothNumber}`}
                  points={area.points}
                  fill="rgba(159, 166, 178, 0.15)"
                  stroke="rgba(159, 166, 178, 0.6)"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {mappedBooths.map((booth) => {
                const key = boothKeyOf(booth);
                return (
                  <polygon
                    key={key}
                    points={booth.points}
                    className={areaClassName(booth, {
                      matches: matchesById[key],
                      selected: key === selectedId,
                      hit: key === hitId,
                      hovered: key === hoveredId,
                    })}
                    style={key === hoveredId && key !== selectedId ? { strokeWidth: 2.5 } : undefined}
                  />
                );
              })}
            </svg>

            {/* Layer 2: plan image */}
            <img
              src={planUrl}
              alt={t('Hall plan')}
              draggable={false}
              className="booth-map__plan-image"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                mixBlendMode: 'multiply',
                objectFit: 'fill',
              }}
            />

            {/* Layer 3: hit polygons + labels */}
            <svg viewBox={viewBoxAttr} preserveAspectRatio="none" style={{ position: 'absolute', inset: 0 }}>
              {/* unassigned areas: hover only (not bookable), drawn first so booth polygons stay on top */}
              {otherAreas.map((area) => {
                const areaKey = unassignedAreaKey(area);
                return (
                  <polygon
                    key={`unassigned-hit-${areaKey}`}
                    points={area.points}
                    fill="transparent"
                    stroke="none"
                    className="hit-off"
                    style={{ cursor: 'not-allowed' }}
                    onMouseEnter={() => {
                      if (onHover) onHover(null);
                      setHoveredAreaId(areaKey);
                    }}
                    onMouseLeave={() => setHoveredAreaId((current) => (current === areaKey ? null : current))}
                  />
                );
              })}
              {mappedBooths.map((booth) => {
                const key = boothKeyOf(booth);
                return (
                <polygon
                  key={key}
                  points={booth.points}
                  fill="transparent"
                  stroke="none"
                  style={{ cursor: 'pointer' }}
                  role="button"
                  tabIndex={0}
                  aria-label={t('Booth {{number}}: {{status}}', { number: booth.boothNumber, status: getStatusLabel(t, booth.status, bookingOpen) })}
                  onClick={() => onSelect && onSelect(booth)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      if (onSelect) onSelect(booth);
                    }
                  }}
                  onMouseEnter={() => {
                    setHoveredAreaId(null);
                    if (onHover) onHover(key);
                  }}
                  onMouseLeave={() => onHover && onHover(null)}
                />
                );
              })}
              {mappedBooths.map((booth) => {
                  const key = boothKeyOf(booth);
                  const position = labelPositions[key];
                  if (!position) return null;
                  // fitted into the booth instead of booth.labelSize (the import's size is ~3 px on screen)
                  const fontSize = fittedLabelSize(booth.boothNumber, position.bounds, pxPerUnit, width / 120);
                  return (
                    <text
                      key={`label-${key}`}
                      x={position.x}
                      y={position.y}
                      className="booth-map__label"
                      style={{ fontSize, strokeWidth: fontSize / 5 }}
                    >
                      {booth.boothNumber}
                    </text>
                  );
                })}
            </svg>
          </div>
        </div>

        {renderLegend('booth-map__legend')}
      </div>

      <div className="mt-2" style={{ minHeight: '1.5rem' }}>
        {renderHoverLine()}
      </div>

      {unmappedBooths.length > 0 && (
        <div className="mt-3">
          <div className="small fw-bold mb-2">{t('Booths without a position on the hall plan')}</div>
          {renderBoothButtons(unmappedBooths)}
        </div>
      )}
    </div>
  );
}
